import type { CSSProperties, ElementType, FC, ReactNode } from 'react'

import { useRef } from 'react'
import { motion, useInView } from 'motion/react'
import { cn } from '@/lib/utils'

export const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const
export const EASE_IN_OUT = [0.83, 0, 0.17, 1] as const

interface MaskLinesProps {
    lines: ReactNode[]
    as?: ElementType
    className?: string
    lineClassName?: string
    delay?: number
    stagger?: number
    duration?: number
    once?: boolean
    immediate?: boolean
}

// Each line slides up from behind its own mask. Lines are passed explicitly so
// nothing has to be measured on the client.
export const MaskLines: FC<MaskLinesProps> = ({
    lines,
    as: Tag = 'div',
    className,
    lineClassName,
    delay = 0,
    stagger = 0.08,
    duration = 1.1,
    once = true,
    immediate = false
}): ReactNode => {
    // The lines start hidden behind their masks, which the browser treats as off
    // screen, so visibility is measured on the whole block instead.
    const trigger = immediate
        ? { animate: 'shown' }
        : { whileInView: 'shown', viewport: { once, amount: 0.1 } }

    return (
        <Tag className={className}>
            <motion.span className='block' initial='hidden' {...trigger}>
                {lines.map((line, index) => (
                    <span key={index} className={cn('block overflow-hidden pb-[0.06em] -mb-[0.06em]', lineClassName)}>
                        <motion.span
                            className='block origin-[0%_100%] will-change-transform'
                            variants={{
                                hidden: { y: '112%', rotate: 2.5 },
                                shown: { y: '0%', rotate: 0, transition: { duration, ease: EASE_OUT_EXPO, delay: delay + index * stagger } }
                            }}
                        >
                            {line}
                        </motion.span>
                    </span>
                ))}
            </motion.span>
        </Tag>
    )
}

interface RevealProps {
    children: ReactNode
    className?: string
    delay?: number
    y?: number
    as?: 'div' | 'p' | 'span' | 'li' | 'figure'
    style?: CSSProperties
}

// A quiet entrance for supporting copy: a short rise with a clip from below.
export const Reveal: FC<RevealProps> = ({ children, className, delay = 0, y = 28, as = 'div', style }): ReactNode => {
    const Component = motion[as]

    return (
        <Component
            className={className}
            style={style}
            initial={{ opacity: 0, y, clipPath: 'inset(0% 0% 18% 0%)' }}
            whileInView={{ opacity: 1, y: 0, clipPath: 'inset(0% 0% 0% 0%)' }}
            viewport={{ once: true, margin: '0px 0px -10% 0px' }}
            transition={{ duration: 1, ease: EASE_OUT_EXPO, delay }}
        >
            {children}
        </Component>
    )
}

interface SplitCharsProps {
    text: string
    className?: string
    charClassName?: string
    delay?: number
    stagger?: number
    immediate?: boolean
}

// Character-level entrance for short display words.
export const SplitChars: FC<SplitCharsProps> = ({
    text,
    className,
    charClassName,
    delay = 0,
    stagger = 0.035,
    immediate = false
}): ReactNode => {
    const chars = Array.from(text)
    const ref = useRef<HTMLSpanElement>(null)
    const inView = useInView(ref, { once: true, margin: '0px 0px -10% 0px' })
    const shown = immediate || inView

    return (
        <span ref={ref} className={cn('relative inline-flex overflow-hidden pb-[0.08em] -mb-[0.08em]', className)}>
            <span className='sr-only'>
                {text}
            </span>
            {chars.map((char, index) => (
                <motion.span
                    key={`${char}-${index}`}
                    aria-hidden
                    className={cn('inline-block will-change-transform', charClassName)}
                    initial={{ y: '110%' }}
                    animate={shown ? { y: '0%' } : { y: '110%' }}
                    transition={{ duration: 1, ease: EASE_OUT_EXPO, delay: delay + index * stagger }}
                >
                    {char === ' ' ? ' ' : char}
                </motion.span>
            ))}
        </span>
    )
}
